/**
 * ENHANCED: Helper functions to merge gap-bridging experiences into resume data
 * NOW SUPPORTS MULTIPLE EXPERIENCES OF THE SAME TYPE
 */

/**
 * Merge additional experiences from gap analysis into reviewedData
 * ENHANCED: Now handles arrays of experiences for each type
 * @param {Object} reviewedData - Original reviewed resume data
 * @param {Object} additionalExperiences - Experiences from gap analysis flow
 *   Structure: { type: dataObject } or { type: [dataObject1, dataObject2, ...] }
 * @returns {Object} Enhanced resume data with additional experiences merged
 */
export function mergeAdditionalExperiences(reviewedData, additionalExperiences) {
  if (!additionalExperiences || Object.keys(additionalExperiences).length === 0) {
    console.log('⚠️ No additional experiences to merge');
    return reviewedData;
  }

  console.log('🔄 Merging additional experiences:', Object.keys(additionalExperiences));

  const enhanced = { ...reviewedData };
  
  // Initialize arrays if they don't exist
  if (!enhanced.experience) enhanced.experience = [];
  if (!enhanced.projects) enhanced.projects = [];
  if (!enhanced.certifications) enhanced.certifications = [];

  let totalAdded = 0;

  // Process each experience type
  Object.entries(additionalExperiences).forEach(([type, data]) => {
    if (!data) {
      console.log(`⚠️ No data for type: ${type}`);
      return;
    }

    // Handle both single object and array of objects
    const dataArray = Array.isArray(data) ? data : [data];
    
    console.log(`✅ Processing ${dataArray.length} experience(s) of type: ${type}`);

    dataArray.forEach((item, index) => {
      if (!item || Object.keys(item).length === 0) {
        console.log(`⚠️ Empty data at index ${index} for type: ${type}`);
        return;
      }

      // ENHANCED: Deduplication check
      const isDuplicate = (list, company, title, dates) => {
        return list.some(existing => 
          (existing.company?.toLowerCase() === company?.toLowerCase() || existing.organization?.toLowerCase() === company?.toLowerCase()) &&
          (existing.position?.toLowerCase() === title?.toLowerCase() || existing.title?.toLowerCase() === title?.toLowerCase() || existing.role?.toLowerCase() === title?.toLowerCase()) &&
          (existing.dates === dates || existing.duration === dates)
        );
      };

      switch (type) {
        case 'freelancing': {
          const company = item.client || item.company || 'Freelance';
          const position = item.projectTitle || item.position || 'Freelance Consultant';
          const dates = item.duration || item.dates || '';
          
          if (isDuplicate(enhanced.experience, company, position, dates)) {
            console.log(`  ⏭️ Skipping duplicate freelancing: ${position}`);
            return;
          }

          enhanced.experience.push({
            company,
            position,
            dates,
            location: item.location || 'Remote',
            bullets: parseBullets(item.achievements || item.bullets),
          });
          totalAdded++;
          console.log(`  ✓ Added freelancing: ${position}`);
          break;
        }

        case 'internship': {
          const company = item.company || '';
          const position = item.position || 'Intern';
          const dates = item.duration || item.dates || '';

          if (isDuplicate(enhanced.experience, company, position, dates)) {
            console.log(`  ⏭️ Skipping duplicate internship: ${position}`);
            return;
          }

          enhanced.experience.push({
            company,
            position,
            dates,
            location: item.location || '',
            bullets: parseBullets(item.achievements || item.bullets),
          });
          totalAdded++;
          console.log(`  ✓ Added internship: ${position} at ${company}`);
          break;
        }

        case 'probono': {
          const company = item.organization || item.company || 'Volunteer Organization';
          const title = item.role || item.title || item.position || 'Volunteer';
          const dates = item.duration || item.dates || '';

          if (isDuplicate(enhanced.projects, company, title, dates)) {
            console.log(`  ⏭️ Skipping duplicate pro-bono: ${title}`);
            return;
          }

          enhanced.projects.push({
            company,
            title,
            position: title,
            dates,
            bullets: parseBullets(item.achievements || item.bullets),
          });
          totalAdded++;
          console.log(`  ✓ Added pro-bono: ${title}`);
          break;
        }

        case 'portfolio': {
          const company = item.company || 'Personal Project';
          const title = item.projectName || item.title || 'Portfolio Project';
          const dates = item.duration || item.dates || '';

          if (isDuplicate(enhanced.projects, company, title, dates)) {
            console.log(`  ⏭️ Skipping duplicate portfolio: ${title}`);
            return;
          }

          enhanced.projects.push({
            company,
            title,
            position: title,
            dates,
            bullets: parseBullets(item.achievements || item.description || item.bullets),
          });
          totalAdded++;
          console.log(`  ✓ Added portfolio: ${title}`);
          break;
        }

        case 'certification': {
          const name = item.certificationName || item.name || '';
          const issuer = item.issuer || item.organization || '';
          const date = item.completionDate || item.date || '';

          const certDuplicate = enhanced.certifications?.some(c => 
            (c.name?.toLowerCase() === name?.toLowerCase()) && 
            (c.issuer?.toLowerCase() === issuer?.toLowerCase())
          );

          if (certDuplicate) {
            console.log(`  ⏭️ Skipping duplicate certification: ${name}`);
            return;
          }

          if (!enhanced.certifications) enhanced.certifications = [];
          enhanced.certifications.push({
            name,
            issuer,
            date,
            skills: item.relevantSkills || item.skills || '',
            isAdditional: true
          });
          totalAdded++;
          console.log(`  ✓ Added certification: ${name}`);
          break;
        }

        case 'minortasks': {
          const company = item.organization || item.company || '';
          const position = item.position || 'Additional Responsibilities';
          const dates = item.timeframe || item.dates || '';

          if (isDuplicate(enhanced.experience, company, position, dates)) {
            console.log(`  ⏭️ Skipping duplicate minor task: ${position}`);
            return;
          }

          enhanced.experience.push({
            company,
            position,
            dates,
            location: item.location || '',
            bullets: parseBullets(item.achievements || item.taskDescription || item.bullets),
            isAdditional: true,
            experienceType: 'minortasks'
          });
          totalAdded++;
          console.log(`  ✓ Added minor tasks: ${item.taskDescription || position}`);
          break;
        }

        default:
          console.warn(`⚠️ Unknown experience type: ${type}`);
      }
    });
  });

  console.log(`✅ Total gap-bridging experiences added: ${totalAdded}`);
  console.log(`📊 Final counts - Experience: ${enhanced.experience.length}, Projects: ${enhanced.projects.length}, Certifications: ${enhanced.certifications?.length || 0}`);

  return enhanced;
}

/**
 * Parse bullets from text (handles both string and array)
 */
function parseBullets(text) {
  if (!text) return [];
  if (Array.isArray(text)) {
    // Filter out empty strings
    return text.filter(b => b && b.trim().length > 0);
  }
  
  // Split by newlines and bullet markers
  const bullets = text
    .split(/\n|•|–|—/)
    .map(b => b.trim())
    .filter(b => b.length > 0);
  
  return bullets;
}

/**
 * Add career roadmap items to resume context
 * This can be used to inform the AI about user's commitment to growth
 */
export function formatCareerRoadmap(careerRoadmap) {
  if (!careerRoadmap || careerRoadmap.length === 0) {
    return '';
  }

  const roadmapText = careerRoadmap
    .map(item => `- Currently pursuing: ${item.title}`)
    .join('\n');

  return `\n\nCareer Development Plan:\n${roadmapText}`;
}

/**
 * Create a prompt addition for AI to emphasize additional experiences
 * ENHANCED: Counts total number of experiences added
 */
export function createAdditionalExperiencePrompt(additionalExperiences) {
  if (!additionalExperiences || Object.keys(additionalExperiences).length === 0) {
    return '';
  }

  const types = [];
  let totalCount = 0;

  Object.entries(additionalExperiences).forEach(([type, data]) => {
    if (!data) return;
    
    const dataArray = Array.isArray(data) ? data : [data];
    const count = dataArray.length;
    totalCount += count;
    
    const typeLabel = getTypeLabel(type, count);
    types.push(typeLabel);
  });

  const experienceTypes = types.join(', ');

  return `\n\nIMPORTANT: The candidate has added ${totalCount} gap-bridging experience(s): ${experienceTypes}. Please prominently feature ALL of these in the resume as they demonstrate commitment to the career transition and directly address skill gaps for the target role. Do NOT omit any of these experiences.`;
}

/**
 * Get human-readable label for experience type
 */
function getTypeLabel(type, count = 1) {
  const labels = {
    'freelancing': count > 1 ? `${count} freelance projects` : 'freelance work',
    'internship': count > 1 ? `${count} internships` : 'internship experience',
    'probono': count > 1 ? `${count} volunteer experiences` : 'volunteer experience',
    'portfolio': count > 1 ? `${count} personal projects` : 'personal project',
    'certification': count > 1 ? `${count} certifications` : 'recent certification',
    'minortasks': count > 1 ? `${count} additional responsibilities` : 'additional responsibilities'
  };
  
  return labels[type] || type;
}

export default {
  mergeAdditionalExperiences,
  formatCareerRoadmap,
  createAdditionalExperiencePrompt
};
